import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * USAGE:
 * 
 * 
 * <GenericDataTable
        columns={[
            { key: "Id", label: "", sortable: false, type: "checkbox", size: "60px" },
            { key: "SKUProductCode", label: "SKU", sortable: true, size: "1fr" },
            { key: "ProductName", label: "Product Name", sortable: true, size: "2fr" },
            { key: "RegistrationsCount", label: "Registrations", sortable: true, size: "1fr" },
        ]}
        fetchUrl="/umbraco/api/RIMSProducts/FamilyProducts/"
        deleteUrl="/umbraco/api/RIMSProducts/BulkProducts/"
        exportUrl="/umbraco/api/RIMSProducts/ProductsExport/"
        queryParams={{
            familyId: 1,
        }}
        exportFileName={`family-products-export.xlsx`}
        rowTitleKey="ProductName"
        searchPlaceholder="Search Product"
        LoadingComponent={LoadingSpinnerImage}
        getRowHref={({ row, column }) => {

            if (
                column.key === "SKUProductCode" ||
                column.key === "ProductName"
            ) {
                return `/products/?productId=${row.Id}`;
            }

            if (column.key === "RegistrationsCount") {
                return `/country-registrations/product-portfolio/product-registrations/?productId=${row.Id}`;
            }

            return null;

        }}
    />
 * 
 * 
 */




const GenericDataTable = ({
    columns = [],

    fetchUrl,
    deleteUrl,
    exportUrl,

    queryParams = {},
    requestConfig = {},

    showTotalCount = false,
    totalCountUrl = null,

    searchPlaceholder = "Search...",
    exportFileName = "export.xlsx",

    rowIdKey = "Id",
    rowTitleKey = "Name",

    enableSearch = true,
    enableDelete = true,
    enableExport = true,
    enableSelection = true,
    enableInfiniteScroll = true,

    pageParam = "page",
    searchParam = "search",
    sortByParam = "sortBy",
    sortTypeParam = "sortType",

    initialSortColumn = null,
    initialSortOrder = "ASCENDING",

    transformResponse = (result) =>
        Array.isArray(result) ? result : [],

    actions = [],

    LoadingComponent,

    emptyMessage = "No results found",

    onDeleteSuccess,
    onExportSuccess,
    customButtons = [],
    refresh,    // If this state variable changes (from outside) this component refreshes the table
}) => {

    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const [hasMore, setHasMore] = useState(true);

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");

    const [sortColumn, setSortColumn] = useState(initialSortColumn);
    const [sortOrder, setSortOrder] = useState(initialSortOrder);

    const [selectedIds, setSelectedIds] = useState([]);

    const [allLoaded, setAllLoaded] = useState(false);

    const [modalType, setModalType] = useState(null);

    const [loadingAll, setLoadingAll] = useState(false);

    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState(null);

    const [totalCount, setTotalCount] = useState(null);
    const [totalTableSize, setTotalTableSize] = useState(0);
    const [columnMultiplier, setColumnMultiplier] = useState(1);

    const observerRef = useRef(null);
    const mainTableParent = useRef(null);

    /**
     * IMPORTANT:
     * Prevent hook dependency loops caused by object references
     */
    const serializedQueryParams = useMemo(
        () => JSON.stringify(queryParams || {}),
        [queryParams]
    );

    const serializedRequestConfig = useMemo(
        () => JSON.stringify(requestConfig || {}),
        [requestConfig]
    );

    const parsedQueryParams = useMemo(
        () => JSON.parse(serializedQueryParams),
        [serializedQueryParams]
    );

    const parsedRequestConfig = useMemo(
        () => JSON.parse(serializedRequestConfig),
        [serializedRequestConfig]
    );

    const gridTemplateColumns = useMemo(() => {

        return columns
            .map((column) => column.size || "1fr")
            .join(" ");

    }, [columns]);

    /**
     * SEARCH DEBOUNCE
     */
    useEffect(() => {

        const timeout = setTimeout(() => {

            setSearch(searchInput);

        }, 500);

        return () => clearTimeout(timeout);

    }, [searchInput]);

    /**
     * BUILD QUERY PARAMS
     */
    const buildQueryParams = ({
        pageToLoad,
        returnAll = false,
    }) => {

        const params = new URLSearchParams();

        params.append(pageParam, pageToLoad);

        Object.entries(parsedQueryParams).forEach(([key, value]) => {

            if (
                value !== undefined &&
                value !== null
            ) {
                params.append(key, value);
            }

        });

        if (search) {
            params.append(searchParam, search);
        }

        if (sortColumn) {

            params.append(sortByParam, sortColumn);
            params.append(sortTypeParam, sortOrder);

        }

        if (returnAll) {
            params.append("returnAll", "true");
        }

        return params;

    };




    const fetchTotalCount = useCallback(async () => {

        if (!showTotalCount || !totalCountUrl) return;

        try {

            const params = new URLSearchParams();

            Object.entries(parsedQueryParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params.append(key, value);
                }
            });

            if (search) {
                params.append(searchParam, search);
            }



            const url = new URL(totalCountUrl, window.location.origin);

            // preserve existing params in totalCountUrl
            const existingParams = new URLSearchParams(url.search);

            // add new params (search + filters)
            const newParams = new URLSearchParams(params.toString());

            newParams.forEach((value, key) => {
                existingParams.set(key, value);
            });

            // rebuild final URL
            url.search = existingParams.toString();

            const response = await fetch(url.toString(), {
                method: "GET",
                ...parsedRequestConfig,
            });



            if (!response.ok) {
                throw new Error("Failed to fetch total count");
            }

            const result = await response.json();

            const count =
                typeof result === "number"
                    ? result
                    : result?.totalCount ??
                    result?.count ??
                    0;

            setTotalCount(count);

        } catch (err) {
            console.error(err);
            setTotalCount(null);
        }

    }, [
        showTotalCount,
        totalCountUrl,
        search,
        serializedQueryParams,
        serializedRequestConfig,
    ]);



    /**
     * FETCH
     */
    const fetchData = useCallback(async ({
        pageToLoad,
        reset = false,
        returnAll = false,
    }) => {

        try {

            setLoading(true);

            if (showTotalCount && pageToLoad === 1 && !returnAll) {
                fetchTotalCount();
            }

            const params = buildQueryParams({
                pageToLoad,
                returnAll,
            });

            const response = await fetch(
                `${fetchUrl}?${params.toString()}`,
                {
                    method: "GET",
                    ...parsedRequestConfig,
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch");
            }

            const result = await response.json();

            const data =
                transformResponse(result);

            setRows((prev) =>
                reset
                    ? data
                    : [...prev, ...data]
            );

            setHasMore(data.length > 0);

            return data;

        } catch (error) {

            console.error(error);

            return [];

        } finally {

            setLoading(false);
            setInitialLoading(false);

        }

    }, [
        fetchUrl,
        search,
        sortColumn,
        sortOrder,
        serializedQueryParams,
        serializedRequestConfig,
    ]);

    /**
     * INITIAL / REFETCH
     */
    useEffect(() => {

        setRows([]);
        setSelectedIds([]);
        setAllLoaded(false);

        setPage(1);
        setHasMore(true);

        if (showTotalCount) {
            setTotalCount(null);
        }

        fetchData({
            pageToLoad: 1,
            reset: true,
        });

    }, [
        search,
        sortColumn,
        sortOrder,
        refresh,
    ]);

    /**
     * INFINITE SCROLL
     */
    const lastRowRef = useCallback((node) => {

        if (!enableInfiniteScroll) return;

        if (loading) return;

        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        observerRef.current =
            new IntersectionObserver(
                (entries) => {

                    if (
                        entries[0].isIntersecting &&
                        hasMore &&
                        !allLoaded
                    ) {

                        setPage((prevPage) => {

                            const nextPage =
                                prevPage + 1;

                            fetchData({
                                pageToLoad: nextPage,
                            });

                            return nextPage;

                        });

                    }

                },
                {
                    threshold: 0.1,
                    rootMargin: "300px",
                }
            );

        if (node) {
            observerRef.current.observe(node);
        }

    }, [
        loading,
        hasMore,
        allLoaded,
        fetchData,
        enableInfiniteScroll,
    ]);

    /**
     * SORT
     */
    const handleSort = (column) => {

        if (!column.sortable) return;

        setRows([]);
        setSelectedIds([]);
        setAllLoaded(false);

        if (sortColumn === column.key) {

            setSortOrder((prev) =>
                prev === "ASCENDING"
                    ? "DESCENDING"
                    : "ASCENDING"
            );

        } else {

            setSortColumn(column.key);
            setSortOrder("ASCENDING");

        }

    };

    /**
     * ROW SELECT
     */
    const toggleRowSelection = (id) => {

        setSelectedIds((prev) => {

            if (prev.includes(id)) {
                return prev.filter((x) => x !== id);
            }

            return [...prev, id];

        });

    };

    /**
     * SELECT ALL
     */
    const handleGlobalCheckbox = async () => {

        const allCurrentlySelected =
            rows.length > 0 &&
            selectedIds.length === rows.length;

        if (allCurrentlySelected) {

            setSelectedIds([]);

            return;

        }

        if (!allLoaded) {

            try {

                setLoadingAll(true);

                const nextPage = page + 1;

                const allData =
                    await fetchData({
                        pageToLoad: nextPage,
                        returnAll: true,
                    });

                if (
                    !allData ||
                    allData.length === 0
                ) {

                    setAllLoaded(true);
                    setHasMore(false);

                    setSelectedIds(
                        rows.map(
                            (x) => x[rowIdKey]
                        )
                    );

                    return;

                }

                setRows((prev) => {

                    const map = new Map();

                    prev.forEach((x) =>
                        map.set(
                            x[rowIdKey],
                            x
                        )
                    );

                    allData.forEach((x) =>
                        map.set(
                            x[rowIdKey],
                            x
                        )
                    );

                    const merged =
                        Array.from(
                            map.values()
                        );

                    setSelectedIds(
                        merged.map(
                            (x) =>
                                x[rowIdKey]
                        )
                    );

                    return merged;

                });

                setPage(nextPage);

                setHasMore(true);

                setAllLoaded(true);

            } finally {

                setLoadingAll(false);

            }

            return;

        }

        setSelectedIds(
            rows.map((x) => x[rowIdKey])
        );

    };

    /**
     * TEXT CELL
     */
    function TextCell({
        value,
        href,
    }) {

        return (
            <div
                style={{
                    padding: "12px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >

                {href ? (
                    <a
                        href={href}
                        style={{
                            color: "#203864",
                            textDecoration: "none",
                        }}
                    >
                        {value}
                    </a>
                ) : (
                    value
                )}

            </div>
        );

    }

    /**
     * CELL RENDER
     */
    const renderCell = (column, row, enableDelete, enableExport) => {

        /**
         * CHECKBOX
         */
        if (
            column.type === "checkbox" && (enableDelete || enableExport) &&
            enableSelection
        ) {

            return (
                <div
                    style={{
                        padding: "12px",
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <input
                        type="checkbox"
                        checked={selectedIds.includes(
                            row[rowIdKey]
                        )}
                        onChange={() =>
                            toggleRowSelection(
                                row[rowIdKey]
                            )
                        }
                    />
                </div>
            );

        }

        /**
         * CUSTOM COLUMN RENDER
         */
        if (column.render) {

            return column.render({
                row,
                value: row[column.key],
                column,
                selectedIds,
                toggleRowSelection,
            });

        }

        /**
         * DEFAULT TEXT
         */
        return (
            <div
                style={{
                    padding: "12px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {row[column.key]}
            </div>
        );

    };
    /**
     * MODAL DATA
     */
    const selectedRows = rows.filter(
        (x) =>
            selectedIds.includes(
                x[rowIdKey]
            )
    );

    /**
     * CONFIRM ACTION
     */
    const handleConfirmAction =
        async () => {

            try {

                setActionLoading(true);
                setActionError(null);

                const ids =
                    selectedIds.join(",");

                /**
                 * DELETE
                 */
                if (modalType === "delete") {

                    const response = await fetch(`${deleteUrl}${ids}`, { method: "DELETE", });

                    if (!response.ok) {
                        throw new Error("Delete failed");
                    }

                    setRows((prev) =>
                        prev.filter((x) => !selectedIds.includes(x[rowIdKey]))
                    );

                    setSelectedIds([]);

                    if (onDeleteSuccess) {
                        onDeleteSuccess();
                    }

                }

                /**
                 * EXPORT
                 */
                if (modalType === "export") {

                    const response = await fetch(`${exportUrl}${ids}`);

                    if (!response.ok) {
                        throw new Error("Export failed");
                    }

                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");

                    a.href = url;
                    a.download = exportFileName;
                    document.body.appendChild(a);

                    a.click();
                    a.remove();

                    window.URL.revokeObjectURL(url);

                    if (onExportSuccess) {
                        onExportSuccess();
                    }

                }

                setModalType(null);

            } catch (error) {

                console.error(error);

                setActionError(
                    modalType ===
                        "delete"
                        ? "Failed to delete selected items."
                        : "Failed to export selected items."
                );

            } finally {

                setActionLoading(false);

            }

        };

    /**
     * RESET MODAL
     */
    useEffect(() => {

        if (!modalType) {

            setActionError(null);
            setActionLoading(false);

        }

    }, [modalType]);


    /*CALCULATE TOTAL TABLE SIZE AND MULTIPLIER FOR COLUMNS IF SMALLER THAN 100% OF PAGE */
    useEffect(() => {
        if (!mainTableParent.current || !columns?.length) return;

        // Sum all column widths
        const totalSize = columns.reduce(
            (sum, col) => sum + (parseInt(col.size) || 0),
            0
        );

        setTotalTableSize(totalSize);

        // Parent width
        const parentWidth = mainTableParent.current.clientWidth - 20;

        // Calculate multiplier
        const multiplier = totalSize < parentWidth ? parentWidth / totalSize : 1;

        setColumnMultiplier(multiplier);

    }, [columns]);

    return (
        <div
            style={{
                width: "100%",
                height:
                    "calc(100vh - 50px)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
            }}
        >

            {/* TOP BAR */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 0",
                    gap: "12px",
                }}
            >

                {/* SEARCH */}
                {enableSearch && (
                    <div
                        style={{
                            padding: "4px",
                            borderRadius:
                                "15px",
                            border:
                                "1px solid #c0c0c0",
                            width: "40%",
                            display: "flex",
                            alignItems:
                                "center",
                            background:
                                "#fff",
                        }}
                    >

                        <span
                            style={{
                                paddingLeft:
                                    "6px",
                                paddingRight:
                                    "6px",
                                color:
                                    "#777",
                            }}
                        >
                            <i className="fas fa-search"></i>
                        </span>

                        <input
                            type="text"
                            placeholder={
                                searchPlaceholder
                            }
                            value={
                                searchInput
                            }
                            onChange={(
                                e
                            ) =>
                                setSearchInput(
                                    e.target
                                        .value
                                )
                            }
                            style={{
                                width:
                                    "100%",
                                outline:
                                    "none",
                                border:
                                    "none",
                                background:
                                    "transparent",
                            }}
                        />

                    </div>
                )}

                {/* ACTIONS */}
                <div
                    style={{
                        display: "flex",
                        alignItems:
                            "center",
                        gap: "12px",
                    }}
                >

                    {actions.map(
                        (action) => (
                            <button
                                key={
                                    action.label
                                }
                                onClick={() =>
                                    action.onClick(
                                        {
                                            selectedIds,
                                            selectedRows,
                                            rows,
                                        }
                                    )
                                }
                            >
                                {
                                    action.label
                                }
                            </button>
                        )
                    )}

                    {enableDelete && selectedIds && selectedIds.length > 0 && (

                        <span
                            className="pointer"
                            onClick={() => {
                                if (selectedIds.length === 0) return;
                                setModalType("delete");
                            }}
                            style={{
                                color: selectedIds.length === 0
                                    ? "#999"
                                    : "rgb(183, 27, 17)",
                                padding: "7px 20px",
                                border: "1px solid rgb(222, 226, 230)",
                                borderRadius: "15px",
                                backgroundColor: "rgb(248, 249, 250)",
                                fontWeight: 500,
                                display: "inline-flex",
                                alignItems: "center",
                                transition: "0.2s",
                                cursor: selectedIds.length === 0
                                    ? "not-allowed"
                                    : "pointer",
                                boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 2px",
                                opacity: selectedIds.length === 0 ? 0.6 : 1,
                            }}
                        >
                            <i
                                className="fas fa-trash-alt g-mr-8"
                                style={{ marginRight: "8px" }}
                            ></i>

                            Delete
                        </span>



                    )}

                    {enableExport && (
                        <span
                            className="pointer"
                            onClick={() => {
                                if (selectedIds.length === 0) return;
                                setModalType("export");
                            }}
                            style={{
                                color: selectedIds.length === 0
                                    ? "#999"
                                    : "rgb(32, 56, 100)",
                                padding: "7px 20px",
                                border: "1px solid rgb(222, 226, 230)",
                                borderRadius: "15px",
                                backgroundColor: "rgb(248, 249, 250)",
                                fontWeight: 500,
                                display: "inline-flex",
                                alignItems: "center",
                                transition: "0.2s",
                                cursor: selectedIds.length === 0
                                    ? "not-allowed"
                                    : "pointer",
                                boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 2px",
                                opacity: selectedIds.length === 0 ? 0.6 : 1,
                            }}
                        >
                            <i
                                className="fas fa-file-excel g-mr-8"
                                style={{ marginRight: "8px" }}
                            ></i>

                            Export
                        </span>
                    )}


                    {customButtons?.map((button, index) => {
                        // Determine if this specific button should be disabled based on selection
                        const isDisabled = button.requiresSelection && (!selectedIds || selectedIds.length === 0);

                        return (
                            <span key={index}
                                className={isDisabled ? "" : "pointer"}
                                onClick={() => {
                                    if (isDisabled) return;
                                    // Optional: pass selectedIds to your callback if the popup needs to know what was selected
                                    button.callback(selectedIds);
                                }}
                                style={{
                                    color: isDisabled ? "#999" : button.buttonFontColor ? button.buttonFontColor : "rgb(32, 56, 100)",
                                    padding: "7px 20px",
                                    border: "1px solid rgb(222, 226, 230)",
                                    borderRadius: "15px",
                                    backgroundColor: isDisabled ? "rgb(248, 249, 250)" : button.buttonBackground ? button.buttonBackground : "rgb(248, 249, 250)",
                                    fontWeight: 500,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    transition: "0.2s",
                                    cursor: isDisabled ? "not-allowed" : "pointer",
                                    boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 2px",
                                    opacity: isDisabled ? 0.6 : 1,
                                }}
                            >
                                <i
                                    className={`${button.cssIcon} g-mr-8`}
                                    style={{ marginRight: "8px" }}
                                ></i>
                                {button.Name}
                            </span>
                        );
                    })}


                </div>

            </div>




            {/* TOTAL COUNT */}
            {showTotalCount && totalCount !== null && (
                <div
                    style={{
                        textAlign: "right",
                        minHeight: "30px"
                    }}
                >
                    <span
                        style={{
                            padding: "6px 12px",
                            borderRadius: "999px",
                            background: "#f4f6f8",
                            border: "1px solid #d0d7de",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#203864",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Total items: {totalCount}
                    </span>
                </div>
            )}



            <div ref={mainTableParent} style={{ position: "relative", maxWidth: "100%", overflowX: "auto", backgroundColor: "white", borderRadius: "15px", border: "1px solid #e2e8f0" }}>

                {/* HEADER */}
                <div
                    style={{
                        display: "flex",
                        background:
                            "#f8f8f8",
                        position:
                            "sticky",
                        top: 0,
                        zIndex: 10,
                        minWidth: "100%",
                        width: totalTableSize * columnMultiplier,
                    }}
                >

                    {columns.map(
                        (column) => (
                            <div
                                key={column.key}
                                onClick={() =>handleSort(column)}
                                style={{
                                    padding: "14px 12px",
                                    fontWeight: 600,
                                    cursor: column.sortable ? "pointer" : "default",
                                    userSelect: "none",
                                    width: `${(parseInt(column.size) || 0) * columnMultiplier}px`
                                }}
                            >

                                {column.type === "checkbox" && (enableDelete || enableExport) ? (
                                    loadingAll ? (
                                        LoadingComponent ? (
                                            <LoadingComponent
                                                small={
                                                    true
                                                }
                                            />
                                        ) : (
                                            "..."
                                        )
                                    ) : (
                                        <input
                                            type="checkbox"
                                            checked={
                                                rows.length >
                                                0 &&
                                                selectedIds.length ===
                                                rows.length
                                            }
                                            onChange={
                                                handleGlobalCheckbox
                                            }
                                        />
                                    )
                                ) : (
                                    <>
                                        {
                                            column.label
                                        }

                                        {sortColumn ===
                                            column.key && (
                                                <span
                                                    style={{
                                                        marginLeft: 8,
                                                    }}
                                                >
                                                    {sortOrder ===
                                                        "ASCENDING"
                                                        ? "↑"
                                                        : "↓"}
                                                </span>
                                            )}
                                    </>
                                )}

                            </div>
                        )
                    )}

                </div>

                {/* ROWS */}
                <div
                    style={{
                        overflowY: "auto",
                        flex: 1,
                        minWidth: "100%",
                        width: totalTableSize * columnMultiplier,
                    }}
                >

                    {initialLoading ? (

                        LoadingComponent ? (
                            <LoadingComponent />
                        ) : (
                            <div>
                                Loading...
                            </div>
                        )

                    ) : (
                        <>
                            {rows.map(
                                (
                                    row,
                                    index
                                ) => {

                                    const isLast =
                                        index ===
                                        rows.length -
                                        1;

                                    return (
                                        <div
                                            key={
                                                row[
                                                rowIdKey
                                                ]
                                            }
                                            ref={
                                                isLast
                                                    ? lastRowRef
                                                    : null
                                            }
                                            className="datatable-row"
                                            style={{
                                                display: "flex",
                                                borderBottom: "1px solid #f1f1f1",
                                            }}
                                        >

                                            {columns.map((column) => (
                                                    <div
                                                        key={column.key}
                                                        style={{
                                                            width: `${(parseInt(column.size) || 0) * columnMultiplier}px`
                                                        }}
                                                    >
                                                        {renderCell(
                                                            column,
                                                            row,
                                                            enableDelete,
                                                            enableExport
                                                        )}
                                                    </div>
                                                )
                                            )}

                                        </div>
                                    );

                                }
                            )}

                            {loading &&
                                rows.length >
                                0 && (
                                    LoadingComponent ? (
                                        <LoadingComponent />
                                    ) : (
                                        <div>
                                            Loading...
                                        </div>
                                    )
                                )}

                            {!loading &&
                                rows.length ===
                                0 && (
                                    <div
                                        style={{
                                            padding:
                                                "24px",
                                            textAlign:
                                                "center",
                                        }}
                                    >
                                        {
                                            emptyMessage
                                        }
                                    </div>
                                )}
                        </>
                    )}

                </div>

            </div>



            {/* MODAL */}
            {modalType && (
                <div
                    style={{
                        position:
                            "fixed",
                        inset: 0,
                        background:
                            "rgba(0,0,0,0.4)",
                        display:
                            "flex",
                        justifyContent:
                            "center",
                        alignItems:
                            "center",
                        zIndex: 9999,
                    }}
                >

                    <div
                        style={{
                            width:
                                "600px",
                            background:
                                "#fff",
                            borderRadius:
                                "8px",
                            padding:
                                "20px",
                        }}
                    >

                        {/* HEADER */}
                        <div
                            style={{
                                display:
                                    "flex",
                                justifyContent:
                                    "space-between",
                                marginBottom:
                                    "20px",
                            }}
                        >

                            <h3
                                style={{
                                    margin: 0,
                                }}
                            >
                                {modalType ===
                                    "delete"
                                    ? `Delete ${selectedRows.length} selected item(s)`
                                    : `Export ${selectedRows.length} selected item(s)`
                                }
                            </h3>


                            <button
                                className="close-button"
                                onClick={() =>
                                    setModalType(
                                        null
                                    )
                                }
                            >
                                <i className="fas fa-times"></i>
                            </button>

                        </div>

                        {/* BODY */}
                        <div
                            style={{
                                maxHeight:
                                    "250px",
                                overflowY:
                                    "auto",
                            }}
                        >

                            {selectedRows.map(
                                (row) => (
                                    <div
                                        key={
                                            row[
                                            rowIdKey
                                            ]
                                        }
                                        style={{
                                            marginBottom:
                                                "10px",
                                            padding:
                                                "5px",
                                            background:
                                                "#f2f2f2",
                                        }}
                                    >
                                        {
                                            row[
                                            rowTitleKey
                                            ]
                                        }
                                    </div>
                                )
                            )}

                        </div>

                        {/* ERROR */}
                        {actionError && (
                            <div
                                style={{
                                    color: "red",
                                    marginTop: "15px", 
                                }}
                            >
                                {
                                    actionError
                                }
                            </div>
                        )}

                        {/* ACTIONS */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: "12px",
                                marginTop: "20px",
                            }}
                        >

                            <button
                                className="action-button save-button"
                                onClick={handleConfirmAction}
                                disabled={actionLoading}
                                style={{
                                    padding: "10px 18px",
                                    cursor: actionLoading ? "not-allowed" : "pointer",
                                    opacity: actionLoading ? 0.7 : 1,
                                    minWidth: "120px",
                                }}
                            >
                                {actionLoading ? (
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "8px",
                                        }}
                                    >
                                        <LoadingComponent small={true} />
                                        Processing...
                                    </span>
                                ) : (
                                    modalType === "delete"
                                        ? "Delete"
                                        : "Export"
                                )}
                            </button>





                        </div>

                    </div>

                </div>
            )}

        </div>
    );
};

export default GenericDataTable;